import { useCallback, useEffect, useState } from 'react';
import { PRODUCT_NAME } from '@goanna-im/shared';
import { logout } from '../lib/auth.js';
import type { AuthUser } from '../lib/auth.js';
import { AdminObserve } from './AdminObserve.js';
import { AdminLibrary } from './AdminLibrary.js';
import { AdminArchives } from './AdminArchives.js';

interface Producer {
  id: string;
  username: string;
  displayName: string;
  role: 'admin' | 'producer';
  active: boolean;
  createdAt: number;
}
interface LiveSession {
  roomCode: string;
  owner: string;
  title: string;
  createdAt: number;
  expiresAt: number;
  presenters: number;
  producers: number;
}

/** Compact presenter-window readout: a full phrase + whether it's expired. */
function accessRemaining(expiresAt: number, now: number): { label: string; expired: boolean } {
  if (!expiresAt) return { label: 'starts when talent connects', expired: false };
  const ms = expiresAt - now;
  if (ms <= 0) return { label: 'closed', expired: true };
  const mins = Math.floor(ms / 60000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return { label: `closes in ${h > 0 ? `${h}h ${m}m` : `${m}m`}`, expired: false };
}

async function api(path: string, method = 'GET', body?: unknown) {
  const res = await fetch(path, {
    method,
    credentials: 'include',
    headers: body ? { 'content-type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error ?? 'Request failed');
  return data;
}

export function AdminPage({
  navigate,
  user,
  onLogout,
}: {
  navigate: (to: string) => void;
  user: AuthUser;
  onLogout: () => void;
}) {
  const [producers, setProducers] = useState<Producer[]>([]);
  const [sessions, setSessions] = useState<LiveSession[]>([]);
  const [now, setNow] = useState(() => Date.now());
  const [observing, setObserving] = useState<LiveSession | null>(null);
  const [msg, setMsg] = useState('');
  const [form, setForm] = useState({ username: '', password: '', displayName: '' });

  const refresh = useCallback(async () => {
    try {
      const p = (await api('/api/admin/producers')) as { producers: Producer[] };
      setProducers(p.producers);
      const s = (await api('/api/admin/sessions')) as { sessions: LiveSession[] };
      setSessions(s.sessions);
    } catch (e) {
      setMsg(String((e as Error).message));
    }
  }, []);

  useEffect(() => {
    void refresh();
    const t = setInterval(refresh, 5000);
    const clock = setInterval(() => setNow(Date.now()), 30000);
    return () => {
      clearInterval(t);
      clearInterval(clock);
    };
  }, [refresh]);

  const create = async () => {
    try {
      await api('/api/admin/producers', 'POST', form);
      setForm({ username: '', password: '', displayName: '' });
      setMsg(`Created producer "${form.username}".`);
      await refresh();
    } catch (e) {
      setMsg(String((e as Error).message));
    }
  };

  const [editing, setEditing] = useState<Producer | null>(null);
  const [edit, setEdit] = useState({ displayName: '', username: '', active: true, password: '' });

  const openEdit = (p: Producer) => {
    setEditing(p);
    setEdit({ displayName: p.displayName, username: p.username, active: p.active, password: '' });
  };

  const saveEdit = async () => {
    if (!editing) return;
    const body: Record<string, unknown> = {
      displayName: edit.displayName,
      username: edit.username,
      active: edit.active,
    };
    if (edit.password.trim()) body.password = edit.password.trim();
    try {
      await api(`/api/admin/producers/${editing.id}`, 'POST', body);
      setMsg(`Saved "${edit.username}".`);
      setEditing(null);
      await refresh();
    } catch (e) {
      setMsg(String((e as Error).message));
    }
  };

  return (
    <div className="wrap stack" style={{ paddingTop: '1rem' }}>
      <header className="row" style={{ justifyContent: 'space-between' }}>
        <div className="brand">
          {PRODUCT_NAME} <small style={{ display: 'inline', color: 'var(--muted)' }}>Super Admin</small>
        </div>
        <div className="row">
          <span className="tag">{user.displayName}</span>
          <button
            onClick={async () => {
              await logout();
              onLogout();
              navigate('/');
            }}
          >
            Sign out
          </button>
        </div>
      </header>

      {msg && (
        <div className="card" style={{ borderColor: 'var(--accent-dim)' }}>
          {msg} <button style={{ marginLeft: '0.5rem' }} onClick={() => setMsg('')}>×</button>
        </div>
      )}

      <div className="console">
        <div className="stack">
          <section className="card stack">
            <h3>Add producer</h3>
            <input
              placeholder="Username"
              value={form.username}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
            />
            <input
              placeholder="Display name (optional)"
              value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })}
            />
            <input
              placeholder="Temporary password (min 6)"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
            <button
              className="primary"
              disabled={form.username.length < 2 || form.password.length < 6}
              onClick={create}
            >
              Create producer
            </button>
          </section>

          <section className="card stack">
            <h3>Producers ({producers.length})</h3>
            {producers.map((p) => (
              <div key={p.id} className="row" style={{ justifyContent: 'space-between' }}>
                <span>
                  <strong>{p.username}</strong>{' '}
                  <span className="muted">
                    {p.displayName !== p.username ? `· ${p.displayName} ` : ''}
                    {p.role === 'admin' ? '· admin ' : ''}
                    {p.active ? '' : '· disabled'}
                  </span>
                </span>
                <button onClick={() => openEdit(p)}>Edit</button>
              </div>
            ))}
          </section>
        </div>

        <div className="stack">
          <section className="card stack">
            <h3>Live sessions ({sessions.length})</h3>
            {sessions.length === 0 && <p className="muted" style={{ margin: 0 }}>No active sessions.</p>}
            {sessions.map((s) => {
              const acc = accessRemaining(s.expiresAt, now);
              return (
                <div key={s.roomCode} className="row" style={{ justifyContent: 'space-between' }}>
                  <span>
                    <strong className="roomcode" style={{ fontSize: '1.1rem' }}>
                      {s.roomCode}
                    </strong>{' '}
                    <span className="muted">· {s.owner} · {s.title}</span>
                    <br />
                    <span
                      className="muted"
                      style={{ fontSize: '0.75rem', color: acc.expired ? 'var(--danger, #ef4444)' : undefined }}
                    >
                      Presenter access: {acc.label}
                    </span>
                  </span>
                  <span className="row" style={{ gap: '0.4rem' }}>
                    <span className="tag readout">
                      {s.presenters}▣ {s.producers}▤
                    </span>
                    <button onClick={() => setObserving(s)}>Open</button>
                  </span>
                </div>
              );
            })}
            <p className="muted" style={{ fontSize: '0.8rem', margin: 0 }}>
              Room codes are live and auto-refresh. Share a code with talent so they can join at
              /presenter. Presenter access to each script closes 12 hours after the session starts.
            </p>
          </section>

          <AdminLibrary onError={setMsg} />
          <AdminArchives onError={setMsg} />
        </div>
      </div>

      {observing && (
        <AdminObserve
          roomCode={observing.roomCode}
          owner={observing.owner}
          onClose={() => setObserving(null)}
        />
      )}

      {editing && (
        <div className="modal-overlay" onClick={() => setEditing(null)}>
          <div className="modal card stack" onClick={(e) => e.stopPropagation()}>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0 }}>Edit {editing.role === 'admin' ? 'admin' : 'producer'}</h3>
              <button onClick={() => setEditing(null)} aria-label="Close">
                ×
              </button>
            </div>
            <label className="stack" style={{ gap: '0.25rem' }}>
              <span className="muted">Display name</span>
              <input
                value={edit.displayName}
                onChange={(e) => setEdit({ ...edit, displayName: e.target.value })}
              />
            </label>
            <label className="stack" style={{ gap: '0.25rem' }}>
              <span className="muted">Username (login)</span>
              <input
                value={edit.username}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                onChange={(e) => setEdit({ ...edit, username: e.target.value })}
              />
            </label>
            <label className="stack" style={{ gap: '0.25rem' }}>
              <span className="muted">New password</span>
              <input
                value={edit.password}
                placeholder="Leave blank to keep current"
                onChange={(e) => setEdit({ ...edit, password: e.target.value })}
              />
            </label>
            {editing.role !== 'admin' && (
              <label className="row" style={{ gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={edit.active}
                  onChange={(e) => setEdit({ ...edit, active: e.target.checked })}
                  style={{ width: 'auto', minHeight: 0 }}
                />
                <span>Active (can sign in)</span>
              </label>
            )}
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <button onClick={() => setEditing(null)}>Cancel</button>
              <button
                className="primary"
                disabled={
                  edit.username.length < 2 || (edit.password.length > 0 && edit.password.length < 6)
                }
                onClick={saveEdit}
              >
                Save changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
